<template>
  <div v-if="location">
    <base-page-heading
      :title="title"
      show-back-button
    >
      <template #secondary-title>
        <sfs-active-badge :active="!location.deleted_at" />
      </template>
      <template #extra>
        <b-dropdown
          v-if="$can('update', 'Location')"
          variant="primary"
          split
          @click="$bvModal.show('location-edit-modal')"
          class="d-none d-md-inline"
          no-flip
          right
          v-b-tooltip.hover
          title="Edit the Location"
        >
          <template #button-content>
            <i class="fa fa-pencil-alt mr-md-1" />
            <span class="d-none d-md-inline">Edit</span>
          </template>
          <b-dropdown-item
            v-if="showCustomFieldsForm"
            v-b-modal.custom-field-values-form-modal
          >
            <i class="fa fa-pencil-alt mr-1" />
            Edit Additional Info
          </b-dropdown-item>
          <b-dropdown-item
            v-b-modal.location-billing-details-modal
          >
            <i class="fa fa-pencil-alt mr-1" />
            Edit Billing Details
          </b-dropdown-item>
        </b-dropdown>

        <net-suite-form-popover
          v-if="$can('read', 'IntegrationReference')"
          :reference-id="location.id"
          reference-type="Location"
          :current-integration-reference-id="location.netsuite_reference_id"
          button-class="m-0"
        />

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('update', 'Location')"
              v-b-modal.location-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-item
              v-if="showCustomFieldsForm && $can('update', 'Location')"
              v-b-modal.custom-field-values-form-modal
            >
              <i class="fa fa-pencil-alt mr-1" />
              Edit Additional Info
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('update', 'Location')"
              v-b-modal.location-billing-details-modal
            >
              <i class="fa fa-pencil-alt mr-1" />
              Edit Billing Details
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item @click="goToWorkOrders">
            <i class="fa fa-list-ul mr-1" /> View Work Orders
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('destroy', 'Location')"
            @click="toggleActive"
          >
            <i
              v-if="!location.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('create', 'ClientFeedback')"
            @click="() => $router.push({ name: 'new-client-feedback', query: { location_id: location.id } })"
          >
            <i class="far fa-comment-dots mr-1" />
            Gather Feedback
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: location.id, model: 'Location' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>
    <div class="content pt-lg-5">
      <b-row class="mb-3">
        <b-col lg="6">
          <b-row>
            <b-col cols="12">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ clientTranslation }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link
                    v-for="(client, index) in location.clients"
                    :key="client.id"
                    :to="{ name: 'client', params: { id: client.id } }"
                  >
                    {{ client.name }}<span v-if="index != location.clients.length - 1">,</span>
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Manager
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.manager">
                  <b-link :to="{ name: 'user', params: { id: location.manager.id } }">
                    {{ location.manager.name }}
                  </b-link>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Customer ID
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.customer_identifier">
                  {{ location.customer_identifier }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr class="mt-0 mb-3">

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Street
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.street }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  City
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.city }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  County
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.county }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  State
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.state }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Zip Code
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.zip_code }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  District
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.district">
                  {{ location.district }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-collapse
            id="collapse-custom-fields"
            v-if="customFieldData.length > 0"
          >
            <b-row
              class="mb-3"
            >
              <b-col
                cols="12"
                sm="6"
                v-for="(customData, index) in customFieldData"
                :key="index"
              >
                <b-row class="text-muted font-w500 font-size-sm mb-1">
                  <b-col>
                    {{ customData.custom_field?.label }}
                  </b-col>
                </b-row>
                <b-row class="mb-3">
                  <b-col>
                    {{ customData.value }}
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </b-collapse>

          <b-row
            v-if="customFieldData.length > 0"
            class="mb-3"
          >
            <b-col>
              <b-link v-b-toggle:collapse-custom-fields>
                <span class="when-open">Show Less</span><span class="when-closed">Show More</span>
              </b-link>
            </b-col>
          </b-row>
        </b-col>

        <b-col lg="6">
          <hr class="mt-0 mb-3 d-md-none">

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Time Zone
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ location.time_zone }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Floor Type
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.floor_type">
                  {{ floorTypeFormatOptions[location.floor_type] }}
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Sales Floor Footage
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.sales_floor_footage">
                  {{ location.sales_floor_footage || 0 }}
                  <span>sq. ft</span>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Stock Room Footage
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="location.stock_room_footage">
                  {{ location.stock_room_footage || 0 }}
                  <span>sq. ft</span>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              <contact-infos-list
                :list-func="$contactInfosAPI.getLocationsContactInfos"
                :create-func="$contactInfosAPI.createLocationsContactInfos"
                :reference-id="location.id"
                reference-type="Location"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-tabs
            content-class="pt-4 mb-3"
            v-model="activeTab"
          >
            <template #tabs-end>
              <div
                v-show="activeTab === 0"
                class="ml-auto mt-3"
              >
                <b-button
                  variant="outline-secondary"
                  @click="$refs.notesTimeline && $refs.notesTimeline.toggleTimelineForm()"
                >
                  <i class="fa fa-plus mr-1" /> New Note
                </b-button>
              </div>

              <div
                v-show="activeTab === 1"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'Document')"
                  v-b-modal.new-document-modal
                  variant="outline-secondary"
                >
                  <i class="fa fa-plus" />
                </b-button>
              </div>

              <div
                v-show="activeTab === 2"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'TechnicianService')"
                  v-b-modal.technician-service-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Technician Service for this Location"
                >
                  <i class="fa fa-plus mr-1" /> Add Technician Service
                </b-button>
              </div>

              <div
                v-show="activeTab === 3"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'RecurringCharge')"
                  v-b-modal.recurring-charge-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Recurring Charge for this Location"
                >
                  <i class="fa fa-plus" /> Add Charge
                </b-button>
              </div>

              <div
                v-show="activeTab === 4"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'RecurringWorkOrder')"
                  v-b-modal.recurring-work-order-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Create a new Recurring Work Order for this Location"
                >
                  <i class="fa fa-plus mr-1" /> Add Recurring Work Order
                </b-button>
              </div>

              <div
                v-show="activeTab === 5"
                class="ml-auto mt-3"
              >
                <b-button
                  v-if="$can('create', 'Commission')"
                  v-b-modal.commissions-add-modal
                  variant="outline-secondary"
                  v-b-tooltip.hover
                  title="Add a new Commission for this Location"
                >
                  <i class="fa fa-plus mr-1" /> Add Commission
                </b-button>
              </div>
            </template>
            <b-tab active>
              <template #title>
                <number-badge
                  title="Notes"
                  :total="notesTotal"
                />
              </template>
              <notes-timeline
                ref="notesTimeline"
                :list-func="$notesAPI.getLocationsNotes"
                :list-params="{ include_work_order_notes: true }"
                :create-func="$notesAPI.createLocationsNote"
                :reference-id="location.id"
                reference-type="Location"
                hide-new-note-button
                @total-rows-changed="(newTotal) => notesTotal = newTotal"
              />
            </b-tab>

            <b-tab
              title="Documents"
            >
              <documents-table
                :columns="$store.getters.columnsForTable(documentsTableName)"
                :append-to-query="false"
                :show-per-page="false"
                :key="documentsKey"
                :list-provider="$documentsAPI.getLocationsDocuments"
                :reference-id="location.id"
              />
            </b-tab>

            <b-tab title="Technician Services">
              <technician-services-table
                :columns="technicianServicesColumns"
                :filters="{ location_id: Number(location.id) }"
                :append-to-query="false"
                :key="technicianServicesKey"
              />
            </b-tab>

            <b-tab
              v-if="$can('read', 'RecurringCharge')"
              title="Recurring Charges"
            >
              <recurring-charges-table
                :columns="recurringChargesColumns"
                :location-id="Number(location.id)"
                :append-to-query="false"
                :key="recurringChargesKey"
              />
            </b-tab>


            <b-tab
              v-if="$can('read', 'RecurringWorkOrder')"
              title="Recurring Work Orders"
            >
              <recurring-work-orders-table
                :columns="recurringWorkOrdersColumns"
                :location-id="Number(location.id)"
                :append-to-query="false"
                :key="recurringWorkOrdersKey"
              />
            </b-tab>

            <b-tab
              v-if="$can('read', 'Commission')"
              title="Commissions"
            >
              <commissions-table
                :columns="commissionsColumns"
                :location-id="Number(location.id)"
                :append-to-query="false"
                :key="commissionsKey"
              />
            </b-tab>

            <b-tab
              v-if="$can('read', 'WorkOrder')"
            >
              <template #title>
                <number-badge
                  title="History"
                  :total="historyTotal"
                />
              </template>
              <work-order-history-list-filter
                @filters-applied="applyHistoryFilters"
              />
              <work-order-history-table
                :location-id="Number(location.id)"
                :filters="historyFilters"
                :append-to-query="false"
                :key="historyKey"
                @total-rows-changed="(newTotal) => historyTotal = newTotal"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <edit-modal
      v-if="$can('update', 'Location')"
      v-model="location"
    />
    <custom-field-values-form-modal
      v-if="$can('update', 'Location')"
      :value="location"
      model-type="Location"
      :custom-field-values="customFieldData"
      @data-changed="(customFields) => showCustomFieldsForm = customFields.length > 0"
      @input="(values) => customFieldData = values"
    />
    <locations-billing-details-modal
      v-if="$can('update', 'Location')"
      v-model="location"
    />
    <new-recurring-charge-modal
      v-if="$can('create', 'RecurringCharge')"
      :location-id="location.id"
      :location="location"
      @on-save="refreshRecurringCharges()"
      :key="recurringChargesKey"
    />
    <new-recurring-work-order-modal
      v-if="$can('create', 'RecurringWorkOrder')"
      :location-id="location.id"
      :location="location"
      @on-save="(recurringWorkOrder) => refreshRecurringWorkOrders(recurringWorkOrder)"
      :key="recurringWorkOrdersKey"
    />
    <new-technician-service-modal
      v-if="$can('create', 'TechnicianService')"
      :location_id="location.id"
      @save="refreshTechnicianServices()"
      :key="technicianServicesKey"
    />
    <new-commission-modal
      v-if="$can('create', 'Commission')"
      :location_id="location.id"
      @save="refreshCommissions()"
      :key="commissionsKey"
    />
    <new-document-modal
      v-if="$can('create', 'Document')"
      :reference-id="location.id"
      :create-provider="$documentsAPI.createLocationsDocument"
      @on-save="refreshDocuments()"
      internal
      :key="documentsKey"
    />
  </div>
</template>

<script>
import EditModal from '@/components/admin/locations/EditModal.vue';
import TechnicianServicesTable from '@/components/admin/technicianServices/Table.vue';
import CommissionsTable from '@/components/admin/commissions/Table.vue';
import RecurringChargesTable from '@/components/admin/recurringCharges/Table.vue';
import RecurringWorkOrdersTable from '@/components/admin/recurringWorkOrders/Table.vue';
import ContactInfosList from '@/components/admin/contactInfos/CardList.vue';
import NewRecurringChargeModal from '@/components/admin/recurringCharges/NewModal.vue';
import NewRecurringWorkOrderModal from '@/components/admin/recurringWorkOrders/NewModal.vue';
import NewTechnicianServiceModal from '@/components/admin/technicianServices/NewModal.vue';
import NewCommissionModal from '@/components/admin/commissions/NewModal.vue';
import CustomFieldValuesFormModal from '@/components/admin/customFields/CustomFieldValuesFormModal.vue';
import LocationsBillingDetailsModal from '@/components/admin/locations/BillingDetailsModal.vue';
import NotesTimeline from '@/components/admin/notes/Timeline.vue';
import NumberBadge from '@/components/shared/NumberBadge.vue';
import NetSuiteFormPopover from '@/components/admin/integrationReferences/NetSuiteFormPopover.vue';
import NewDocumentModal from '@/components/admin/documents/NewModal.vue';
import DocumentsTable from '@/components/admin/documents/Table.vue';
import WorkOrderHistoryListFilter from '@/components/admin/workOrders/HistoryListFilter.vue';
import WorkOrderHistoryTable from '@/components/admin/workOrders/HistoryTable.vue';
import { LOCATION_FLOOR_TYPE_FORMAT_OPTIONS } from '@/constants/locations';
import { DOCUMENTS_TABLE_NAME } from '@/constants/documents';
import { TECHNICIAN_SERVICES_TABLE_NAME } from '@/constants/technicianServices';
import { COMMISSION_TABLE_NAME } from '@/constants/commissions';
import { RECURRING_CHARGE_TABLE_NAME } from '@/constants/recurringCharges';
import { RECURRING_WORK_ORDER_TABLE_NAME } from '@/constants/recurringWorkOrders';
import pluralize from 'pluralize';
import { DateTime } from 'luxon';

export default {
  name: 'LocationsShow',
  components: {
    EditModal,
    TechnicianServicesTable,
    CommissionsTable,
    RecurringChargesTable,
    RecurringWorkOrdersTable,
    ContactInfosList,
    NewRecurringChargeModal,
    NewTechnicianServiceModal,
    NewCommissionModal,
    NewRecurringWorkOrderModal,
    CustomFieldValuesFormModal,
    LocationsBillingDetailsModal,
    NotesTimeline,
    NumberBadge,
    NetSuiteFormPopover,
    NewDocumentModal,
    DocumentsTable,
    WorkOrderHistoryListFilter,
    WorkOrderHistoryTable,
  },
  data() {
    return {
      location: null,
      notesTotal: 0,
      historyTotal: 0,
      activeTab: 0,
      customFieldData: [],
      showCustomFieldsForm: false,
      technicianServicesTableName: TECHNICIAN_SERVICES_TABLE_NAME,
      commissionsTableName: COMMISSION_TABLE_NAME,
      recurringChargesTableName: RECURRING_CHARGE_TABLE_NAME,
      recurringWorkOrdersTableName: RECURRING_WORK_ORDER_TABLE_NAME,
      technicianServicesKey: Date.now(),
      recurringChargesKey: Date.now() + 1,
      recurringWorkOrdersKey: Date.now() + 2,
      commissionsKey: Date.now() + 3,
      floorTypeFormatOptions: LOCATION_FLOOR_TYPE_FORMAT_OPTIONS,
      documentsTableName: DOCUMENTS_TABLE_NAME,
      documentsKey: `documents-${Date.now()}`,
      historyKey: `history-${Date.now()}`,
      historyFilters: {},
    };
  },
  computed: {
    toggleActiveTooltipText() {
      return !this.location.deleted_at ? 'Deactivate' : 'Reactivate';
    },
    toggleActiveConfirmText() {
      return !this.location.deleted_at
        ? 'Are you sure you want to deactivate the Location?'
        : 'Are you sure you want to reactivate the Location?';
    },
    title() {
      let str = this.location.store_number;
      if (this.location.name && this.location.name != this.location.store_number) {
        str += ` - ${this.location.name}`;
      }

      return str;
    },
    technicianServicesColumns() {
      return this.$store.getters.columnsForTable(this.technicianServicesTableName);
    },
    commissionsColumns() {
      return this.$store.getters.columnsForTable(this.commissionsTableName);
    },
    recurringChargesColumns() {
      return this.$store.getters.columnsForTable(this.recurringChargesTableName);
    },
    recurringWorkOrdersColumns() {
      return this.$store.getters.columnsForTable(this.recurringWorkOrdersTableName);
    },
    clientTranslation() {
      return pluralize(this.$store.getters.translate('client', 'Client'));
    },
  },
  mounted() {
    this.getLocation();
    this.getCustomFieldData();
  },
  methods: {
    getLocation() {
      this.$locationsAPI
        .get(this.$route.params.id)
        .then((location) => (this.location = location))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    goToWorkOrders() {
      if (!this.location) { return; }
      this.$router.push({ name: 'work-orders', query: { location_id: String(this.location.id) } });
    },
    getCustomFieldData() {
      this.$customFieldsAPI.getLocationValues(this.$route.params.id).then(({ customFieldValues }) => {
        this.customFieldData = customFieldValues;
      });
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$locationsAPI.update(this.$route.params.id, { deleted_at: !this.location.deleted_at ?  DateTime.now().toISODate() : null }),
      }).then((result) => {
        if (result.value) {
          this.location = result.value;
        }
        result.timer;
      });
    },
    refreshRecurringCharges() {
      this.recurringChargesKey = Date.now();
    },
    refreshRecurringWorkOrders(recurringWorkOrder) {
      this.recurringWorkOrdersKey = Date.now();
      setTimeout(() => this.$bvModal.show(`recurring-work-order-${recurringWorkOrder.id}-frequencies-modal`), 1000);
    },
    refreshTechnicianServices() {
      this.technicianServicesKey = Date.now();
    },
    refreshCommissions() {
      this.commissionsKey = Date.now();
    },
    refreshDocuments() {
      this.documentsKey = `documents-${Date.now()}`;
    },
    applyHistoryFilters({ filters }) {
      this.historyFilters = filters;
    },
  },
};
</script>
